import { storeGroupService } from "@/services/storeGroupService";
import {
  ApiResponse,
  CommonParams,
  CreateMyGroupDto,
  GetGroupsRespDto,
  RoleEnum,
  StoreGroupDto,
} from "@/types";
import { toastError } from "@/utils/toastify";
import { create } from "zustand";

type StoreGroup = {
  groups: StoreGroupDto[];
  group: StoreGroupDto | null;
  groupsInCompany: ApiResponse<{ label: string; value: number }[]>;
  setGroups: (groups: StoreGroupDto[]) => void;
  setGroup: (group: StoreGroupDto) => void;
  setGroupsInCompany: (
    groups: ApiResponse<{ label: string; value: number }[]>
  ) => void;
  createGroup: (data: CreateMyGroupDto) => Promise<ApiResponse<StoreGroupDto>>;
  fetchGroups: () => Promise<void>;
  fetchGroup: (id: number) => Promise<ApiResponse<GetGroupsRespDto>>;
  fetchGroupsInCompany: (
    role: RoleEnum,
    params: CommonParams<{ companyId: number }>
  ) => Promise<ApiResponse<StoreGroupDto[]>>;
  updateGroup: (
    id: number,
    data: { name: string; storeIds: number[] }
  ) => Promise<ApiResponse<null>>;
};

export const useGroupStore = create<StoreGroup>((set) => ({
  groups: [],
  group: null,
  groupsInCompany: {
    data: [],
    status: 200,
    pagination: {
      page: 1,
      hasNextPage: true,
      pageSize: 0,
      itemCount: 0,
      pageCount: 0,
      hasPreviousPage: false,
    },
  },
  setGroups: (groups: StoreGroupDto[]) => set({ groups }),
  setGroup: (group: StoreGroupDto) => set({ group }),
  setGroupsInCompany: (
    groups: ApiResponse<{ label: string; value: number }[]>
  ) => set({ groupsInCompany: groups }),
  createGroup: async (data: CreateMyGroupDto) => {
    try {
      const response = await storeGroupService.createMyStoreGroup(data);
      return response;
    } catch (error: any) {
      throw error;
    }
  },
  fetchGroups: async () => {},
  fetchGroup: async (id: number) => {
    try {
      const response = await storeGroupService.getStoreGroupById(id);
      return response;
    } catch (error: any) {
      toastError(error.message);
      return {
        data: null,
        status: error.response?.data?.status,
        message: error.response?.data?.message,
      } as any;
    }
  },
  fetchGroupsInCompany: async (
    role: RoleEnum,
    params: CommonParams<{ companyId: number }>
  ) => {
    try {
      const functionFetch =
        role === RoleEnum.COMPANY_ADMIN
          ? storeGroupService.getMyStoreGroup
          : storeGroupService.getMyManagerStoreGroups;
      const response = await functionFetch(params);
      const page = response.pagination?.page;

      set(({ groupsInCompany }) => ({
        groupsInCompany: {
          ...response,
          data: [
            ...(page == 1 ? [] : groupsInCompany.data),
            ...response.data.map((i) => ({
              label: i.name,
              value: i.id,
            })),
          ],
        },
      }));
      return response;
    } catch (error: any) {
      return {
        data: [] as any,
        pagination: {
          page: 1,
          pageSize: 100,
          itemCount: 0,
          pageCount: 0,
          hasPreviousPage: false,
          hasNextPage: false,
        },
        status: error.response?.data?.status,
        message: error.response?.data?.message,
      } as any;
    }
  },
  updateGroup: async (
    id: number,
    data: { name: string; storeIds: number[] }
  ) => {
    try {
      const response = await storeGroupService.updateStoreGroup(id, data);
      return response;
    } catch (error: any) {
      throw error;
    }
  },
}));
